import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { CobaltTriangle, OrangeArc, StripedTriangle } from "../components/Motifs";
import { IMG } from "../lib/site";

const roles = [
  "National Board Member, Hostelling International USA",
  "92NY Women InPower Executive Fellow",
  "Shafik Gabr Foundation Fellow (Egypt)",
  "Network 20/20 Entrepreneurial Diplomacy Fellow",
];

const awards = [
  "White House United State of Women Changemaker nominee",
  "SPARK Boston Impact Award for Nonprofit Innovation",
  "Presidential Service Award",
];

const MeetConsultant = () => {
  return (
    <div data-testid="meet-consultant-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 pt-8 lg:pt-10 pb-8 lg:pb-10">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-14 items-center">
            <div className="lg:col-span-6">
              <Reveal>
                <div className="overline">Meet the consultant</div>
              </Reveal>
              <Reveal delay={0.1}>
                <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-[3.5rem] leading-[1.02] tracking-tight text-ink">
                  Leah <em className="italic text-cobalt">Moschella</em>
                </h1>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="mt-5 text-base lg:text-lg text-ink-soft leading-relaxed max-w-xl">
                  Founder of Elenem Strategies. Systems designer, facilitator, strategist, and trusted thought
                  partner helping education, workforce, and community leaders transform ambitious ideas into
                  practical strategies that expand opportunity.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-6 relative">
              <Reveal delay={0.15}>
                <div className="relative">
                  <div className="absolute -top-3 -left-3 w-full h-full stripes-tan -z-10" />
                  <div className="relative aspect-[4/5] w-full max-h-[540px] overflow-hidden">
                    <img
                      src={IMG.speaking}
                      alt="Leah Moschella speaking at a professional event"
                      className="w-full h-full object-cover"
                      style={{ objectPosition: "center center" }}
                      fetchpriority="high"
                      decoding="async"
                    />
                  </div>
                </div>
              </Reveal>
              <OrangeArc className="absolute -top-5 -right-5 hidden md:block" size={90} />
              <CobaltTriangle className="absolute -bottom-5 -right-5 hidden md:block" size={70} flip />
            </div>
          </div>
        </div>
      </section>

      {/* Background */}
      <section className="py-20 lg:py-24 bg-cream-alt relative overflow-hidden">
        <StripedTriangle className="absolute -top-8 -right-8 opacity-50 hidden md:block" size={220} flip />
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            <div className="lg:col-span-8">
              <Reveal>
                <div className="overline">Background</div>
                <h2 className="mt-3 font-display text-3xl lg:text-4xl text-ink leading-tight">
                  Two decades of exploring what's possible — and helping others build it.
                </h2>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="mt-8 space-y-6 text-lg text-ink-soft leading-relaxed">
                  <p>
                    Leah Moschella is a nationally recognized leader at the intersection of education, workforce
                    development, and economic mobility. Throughout her career, she has partnered with states,
                    school systems, entrepreneurs, higher education institutions, employers, foundations, and
                    nonprofits to design learner-centered ecosystems that prepare people for meaningful careers
                    and strengthen communities. Her expertise spans college and career pathways, work-based
                    learning, dual enrollment, talent pipeline development, workforce partnerships, credential
                    innovation, practitioner-informed policy, and cross-sector systems design.
                  </p>
                  <p>
                    From statewide credential initiatives and workforce strategies to leadership collaboratives
                    and international exchanges, Leah's work centers on one question:{" "}
                    <em className="italic text-cobalt not-italic font-medium">
                      How do we create systems that respond to today's challenges while preparing for tomorrow's
                      opportunities?
                    </em>{" "}
                    She believes the people closest to the challenge should help design the solution, and her
                    facilitation style brings together educators, employers, policymakers, entrepreneurs, and
                    community leaders to do exactly that.
                  </p>
                  <p>
                    Leah's work is rooted in exploration. Not because every answer lives somewhere else, but
                    because every new perspective helps us see our own work differently. That belief has taken
                    her to all seven continents, every U.S. state, classrooms around the world, community
                    colleges, boardrooms, and neighborhood organizations.
                  </p>
                  <p className="font-display italic text-2xl text-ink leading-snug">
                    Great ideas travel. They spark conversations. They bring people together. And when curiosity
                    meets community, ideas become action — creating lasting opportunity for learners, employers,
                    and communities.
                  </p>
                </div>
              </Reveal>
            </div>
            {/* Right column: two stacked photos */}
            <div className="lg:col-span-4 lg:sticky lg:top-28 space-y-5">
              <Reveal delay={0.15}>
                <figure className="relative aspect-[4/5] overflow-hidden">
                  <img
                    src={IMG.aboutUn}
                    alt="Leah at the UN General Assembly"
                    className="w-full h-full object-cover"
                    style={{ objectPosition: "18% 85%" }}
                    loading="lazy"
                    decoding="async"
                  />
                  <figcaption className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-3 text-cream text-[11px] tracking-[0.15em] uppercase">
                    UN General Assembly
                  </figcaption>
                </figure>
              </Reveal>
              <Reveal delay={0.25}>
                <figure className="relative aspect-[5/4] overflow-hidden">
                  <img
                    src={IMG.flags}
                    alt="Leah with colleagues in an international setting"
                    className="w-full h-full object-cover"
                    style={{ objectPosition: "75% 35%", filter: "brightness(1.55) contrast(0.98) saturate(1.1)" }}
                    loading="lazy"
                    decoding="async"
                  />
                  <figcaption className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-3 text-cream text-[11px] tracking-[0.15em] uppercase">
                    International collaboration
                  </figcaption>
                </figure>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership, Recognition & Service */}
      <section className="py-20 lg:py-24 bg-cream">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-4">
              <Reveal>
                <div className="overline">Leadership, recognition &amp; service</div>
                <h2 className="mt-4 font-display text-3xl lg:text-4xl text-ink leading-tight">
                  Recognized for <em className="italic text-cobalt">innovation</em>, leadership, and community
                  impact.
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-8 space-y-6 text-lg text-ink-soft leading-relaxed">
              <Reveal delay={0.1}>
                <p>
                  Leah's work has been recognized nationally for innovation, leadership, and community impact.
                  She has authored publications on college and career pathways, work-based learning, credential
                  innovation, regional workforce ecosystems, and governance, and regularly speaks to audiences
                  across the United States on the future of education and work.
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <p>
                  Her experience includes leadership positions at national organizations including the{" "}
                  <span className="text-ink font-medium">National Center on Education and the Economy</span>,{" "}
                  <span className="text-ink font-medium">Jobs for the Future</span>, and the{" "}
                  <span className="text-ink font-medium">Education Design Lab</span>. For twelve years, she
                  served as the founder and Executive Director of{" "}
                  <span className="text-ink font-medium">Girls' Leadership, Organized Women (GLOW)</span> in
                  Boston.
                </p>
              </Reveal>

              <Reveal delay={0.2}>
                <div className="pt-4">
                  <div className="overline text-cobalt">Board service &amp; fellowships</div>
                  <ul className="mt-4 space-y-3" data-testid="fellowships-list">
                    {roles.map((r) => (
                      <li key={r} className="flex items-start gap-3">
                        <span className="mt-2.5 w-1.5 h-1.5 bg-cobalt flex-shrink-0" />
                        <span className="text-ink">{r}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>

              <Reveal delay={0.25}>
                <div className="pt-4">
                  <div className="overline text-orange-brand">Selected honors</div>
                  <ul className="mt-4 space-y-3" data-testid="awards-list">
                    {awards.map((a) => (
                      <li key={a} className="flex items-start gap-3">
                        <span className="mt-2.5 w-1.5 h-1.5 bg-orange-brand flex-shrink-0" />
                        <span className="text-ink">{a}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="mt-3 text-sm text-ink-soft italic">
                    …and numerous leadership and community impact recognitions throughout her career.
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* What it's like to work with Leah */}
      <section className="py-20 lg:py-24 bg-cream-alt relative overflow-hidden">
        <OrangeArc className="absolute -bottom-8 -left-8 hidden md:block" size={140} />
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-4">
              <Reveal>
                <figure className="relative aspect-[4/5] overflow-hidden">
                  <img
                    src={IMG.aboutSmile}
                    alt="Leah at a networking event"
                    className="w-full h-full object-cover"
                    style={{ objectPosition: "70% 20%" }}
                    loading="lazy"
                    decoding="async"
                  />
                  <figcaption className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-3 text-cream text-[11px] tracking-[0.15em] uppercase">
                    Networking &amp; community
                  </figcaption>
                </figure>
              </Reveal>
            </div>
            <div className="lg:col-span-8">
              <Reveal>
                <div className="overline">What it's like to work with Leah</div>
                <h2 className="mt-4 font-display text-3xl lg:text-4xl text-ink leading-tight">
                  Curiosity, clarity, and just enough humor.
                </h2>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="mt-8 space-y-6 text-lg text-ink-soft leading-relaxed">
                  <p>
                    Clients often describe Leah as someone who can quickly connect ideas, build trust across
                    diverse groups, and transform complex challenges into practical next steps. She brings
                    thoughtful facilitation, systems thinking, genuine curiosity, and a sense of optimism to
                    every engagement — along with well-designed slide decks, thoughtful questions, and just
                    enough humor to make even the toughest conversations productive.
                  </p>
                  <p className="font-display italic text-xl lg:text-2xl text-ink leading-snug">
                    When she's not designing education and workforce ecosystems, you'll likely find her planning
                    her next adventure, cheering from the stands at a women's sporting event, applauding a
                    Broadway curtain call, or convincing people that cats look objectively better in bow ties.
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* CTA banner */}
      <section className="py-20 bg-ink text-cream">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <p className="font-display text-3xl lg:text-4xl leading-tight max-w-xl">
            Curious what this could look like for your work?
          </p>
          <Link
            to="/collaborate"
            data-testid="meet-cta"
            className="inline-flex items-center gap-2 bg-orange-brand text-cream px-8 py-4 text-sm tracking-[0.15em] uppercase hover:bg-orange-soft transition-colors"
          >
            Let's talk <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default MeetConsultant;
